import { useEffect, useRef, useState } from 'react';
import Message from './Message';
import MessageInput from './MessageInput';
import { ChatBubbleLeftRightIcon, ArrowDownCircleIcon, HashtagIcon } from '@heroicons/react/24/outline';

/**
 * ChatRoom component that displays messages and handles chat functionality
 * @param {Object[]} messages - Array of message objects to display
 * @param {string} username - Current user's username
 * @param {function} onSendMessage - Callback function to handle sending new messages
 * @param {string} roomName - Name of the current chat room
 * @param {function} onTyping - Callback function to handle typing indicator
 */
function ChatRoom({ messages, username, onSendMessage, roomName, onTyping }) {
  // Create a ref to access the bottom of messages container for auto-scrolling
  const messagesEndRef = useRef(null);
  // Create a ref to access the main chat container element
  const chatContainerRef = useRef(null);
  // State to track if user has scrolled up and new messages are below
  const [showScrollButton, setShowScrollButton] = useState(false);
  // State to track unread messages when user has scrolled up
  const [unreadCount, setUnreadCount] = useState(0);

  /**
   * Function to smoothly scroll the chat to the bottom
   * Uses the messagesEndRef to scroll the invisible div into view
   */
  const scrollToBottom = (behavior = "smooth") => {
    // Optional chaining (?.) in case ref isn't attached yet
    messagesEndRef.current?.scrollIntoView({ behavior });
    if (unreadCount > 0) {
      setUnreadCount(0);
    }
    setShowScrollButton(false);
  };

  // Listen for scroll events to show/hide scroll button
  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      // Check if scrolled up more than 100px from bottom
      const isScrolledUp = container.scrollHeight - container.scrollTop - container.clientHeight > 100;
      setShowScrollButton(isScrolledUp);
      
      // If not showing scroll button, reset unread count
      if (!isScrolledUp) {
        setUnreadCount(0);
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // UseEffect hook to handle new messages and scroll behavior
  useEffect(() => {
    // Get the current scroll position
    const container = chatContainerRef.current;
    if (!container) return;
    
    const isScrolledUp = container.scrollHeight - container.scrollTop - container.clientHeight > 100;
    
    if (isScrolledUp) {
      // If scrolled up and new message arrives, increment unread count
      const lastMessage = messages[messages.length - 1];
      if (lastMessage && lastMessage.username !== username) {
        setUnreadCount(prev => prev + 1);
      }
    } else {
      // If at bottom, scroll to newest message
      scrollToBottom("auto");
    }
  }, [messages, username]); // Dependency array with messages to trigger on message updates

  // Format and group messages by date
  const getMessageDateGroups = () => {
    if (!messages.length) return [];
    
    const groups = [];
    let currentDate = null;
    let currentGroup = [];
    
    // Function to get date string from timestamp
    const getDateString = (timestamp) => {
      const date = new Date(timestamp);
      return date.toLocaleDateString(undefined, { 
        weekday: 'long', 
        month: 'short', 
        day: 'numeric' 
      });
    };
    
    messages.forEach((message) => {
      const messageDate = getDateString(message.timestamp);
      
      if (messageDate !== currentDate) {
        // Start a new group
        if (currentGroup.length) {
          groups.push({
            date: currentDate,
            messages: currentGroup
          });
        }
        
        currentDate = messageDate;
        currentGroup = [message];
      } else {
        // Add to current group
        currentGroup.push(message);
      }
    });
    
    // Add the last group
    if (currentGroup.length) {
      groups.push({
        date: currentDate,
        messages: currentGroup
      });
    }
    
    return groups;
  };

  const messageGroups = getMessageDateGroups();

  return (
    // Main outer container with responsive width and centered content
    <div className="flex-1 container mx-auto px-2 sm:px-4 py-3 sm:py-6 max-w-4xl">
      {/* Chat interface card with styling */}
      <div className="bg-gray-900 rounded-xl shadow-lg overflow-hidden border border-gray-700 h-full">
        {/* Header section of the chat */}
        <div className="bg-gray-800 px-4 py-3 border-b border-gray-700 flex items-center justify-between">
          {/* Left side: Channel info */}
          <div className="flex items-center gap-3">
            <div className="bg-gray-700 p-1.5 rounded-md">
              <HashtagIcon className="h-5 w-5 text-blue-400" />
            </div>
            <div>
              <h2 className="text-base font-medium text-white flex items-center gap-2">
                {roomName || 'Live Chat'}
              </h2>
              <p className="text-xs text-gray-400">
                {messages.length} messages
              </p>
            </div>
          </div>
          {/* Right side actions could go here */}
        </div>

        {/* Main messages area with scrolling capability */}
        <div 
          ref={chatContainerRef}
          // Height calculation accounts for header and input area, with overflow scroll
          className="h-[calc(100vh-280px)] sm:h-[calc(100vh-300px)] overflow-y-auto px-4 py-6 space-y-6 bg-gray-900 scrollable"
        >
          {/* Conditional rendering based on message existence */}
          {messages.length === 0 ? (
            // Empty state display when no messages exist
            <div className="flex flex-col items-center justify-center h-full text-gray-400 space-y-3">
              {/* Larger chat icon for empty state */}
              <div className="bg-gray-800 p-4 rounded-full">
                <ChatBubbleLeftRightIcon className="h-12 w-12 text-gray-600" />
              </div>
              {/* Empty state message */}
              <p className="text-center">
                No messages yet.<br />
                <span className="text-blue-400">Start the conversation!</span>
              </p>
            </div>
          ) : (
            // Render message groups by date
            messageGroups.map((group, groupIndex) => (
              <div key={groupIndex} className="space-y-4">
                {/* Date separator */}
                <div className="flex items-center justify-center my-4">
                  <div className="bg-gray-800 px-3 py-1 rounded-full text-xs text-gray-300 font-medium border border-gray-700">
                    {group.date}
                  </div>
                </div>
                
                {/* Messages in this group */}
                {group.messages.map((message, messageIndex) => (
                  <Message
                    key={message.id || messageIndex}
                    message={message}
                    isOwnMessage={message.username === username}
                    previousMessage={messageIndex > 0 ? group.messages[messageIndex - 1] : null}
                  />
                ))}
              </div>
            ))
          )}
          {/* Invisible div used as scroll target */}
          <div ref={messagesEndRef} />
        </div>

        {/* Scroll to bottom button - only visible when needed */}
        {showScrollButton && (
          <button 
            onClick={() => scrollToBottom()}
            className="absolute right-6 bottom-24 bg-blue-600 text-white rounded-full p-2 shadow-lg z-10 hover:bg-blue-700 transition-all flex items-center justify-center"
            aria-label="Scroll to bottom"
          >
            <ArrowDownCircleIcon className="h-6 w-6" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </button>
        )}

        {/* Bottom section containing message input */}
        <div className="border-t border-gray-700 bg-gray-800 p-3 sm:p-4">
          {/* Message input component with send callback and typing indicator */}
          <MessageInput 
            onSendMessage={onSendMessage} 
            onTyping={onTyping} 
          />
        </div>
      </div>
    </div>
  );
}

export default ChatRoom;